import { collection, doc, setDoc, updateDoc } from "firebase/firestore"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import Form from "@rjsf/mui"
import validator from "@rjsf/validator-ajv8"
import { useEffect, useMemo, useState } from "react"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { PopupService } from "react-ui-components/components/PopupService"
import { GenericListServiceConfig } from "../types"
import { generateUUID, getFileExtension, getSchema, isBase64Image } from "../../utils"
import { storage, auth, db } from "../../firebase"
import { getData } from "../../components/core/CreateEditOptions/getData"
// import { getData } from "./core/CreateEditOptions/getData"

interface AddEditComponentProps {
  close?: () => void
  onAdded?: () => void
  data?: any
  mainId?: string
  Service: GenericListServiceConfig
  onAdd?: (formData: any) => void
  format?: (data: any) => any
}

const removeUndefinedFields = (obj: any[] | null): any => {
  if (obj === null || typeof obj !== "object") {
    return obj
  }

  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map((item) => removeUndefinedFields(item))
      .filter((item) => item !== undefined)
    return cleanedArray.length > 0 ? cleanedArray : undefined
  }

  const cleanedObj: Record<string, any> = {}
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    const cleanedValue = removeUndefinedFields(value)
    if (cleanedValue !== undefined) {
      cleanedObj[key] = cleanedValue
    }
  })

  return Object.keys(cleanedObj).length > 0 ? cleanedObj : undefined
}

const AddEditComponent = ({
  close,
  onAdded,
  data,
  mainId,
  Service,
  onAdd,
  format,
}: AddEditComponentProps) => {
  const [schema, setSchema] = useState<Record<string, any>>({})
  const [formData, setFormData] = useState(data)

  useEffect(() => {
    const hasData = Boolean(data) && Object.keys(data || {}).length > 0
    const shouldFetchExistingData = Boolean(data?.id) && Object.keys(data || {}).length <= 1

    if (shouldFetchExistingData) {
      const path = Service.getListLocation(mainId)
      if (Array.isArray(path)) {
        getData({
          setData: (nextData: any) => {
            setFormData(Service.prepareFormData ? Service.prepareFormData(nextData) : nextData)
          },
          path: [...path, data.id],
        })
      }
      return
    }

    if (hasData) {
      setFormData(Service.prepareFormData ? Service.prepareFormData(data) : data)
    }
  }, [data, mainId, Service])

  useEffect(() => {
    const cols = Service.columnsProperties.filter((column) => !column.hidden)
    if (!cols?.length) {
      return
    }

    const generatedSchema = Service.schema
      ? Service.schema
      : getSchema((data ? Service.EDIT_TITLE : Service.ADD_TITLE) as any, cols as any)
    setSchema({ ...generatedSchema, ...Service.otherProps })
  }, [
    Service.ADD_TITLE,
    Service.EDIT_TITLE,
    Service.columnsProperties,
    Service.otherProps,
    Service.schema,
    data,
  ])

  const handleUpload = async (
    id: any,
    fieldName: any,
    image: string,
    imageInfo: any,
    _imagePropName: any,
  ) => {
    const isBase64 = isBase64Image(image)
    if (!isBase64) {
      return imageInfo
        ? {
            ...imageInfo,
            image,
          }
        : image
    }

    const uid = generateUUID()
    const storageReference = ref(
      storage,
      `${Service.IMAGES_BUCKET}/${id}/${fieldName}-${uid}-${Date.now()}.${getFileExtension(image)}`,
    )

    const response = await fetch(image)
    const blob = await response.blob()
    await uploadBytes(storageReference, blob)

    const downloadURL = await getDownloadURL(storageReference)
    return imageInfo
      ? {
          ...imageInfo,
          image: downloadURL,
        }
      : downloadURL
  }

  const handleSubmit = async (result: { formData: any }) => {
    LoadingService.setLoading(true)
    try {
      const applyFormat = (nextPayload: any) => {
        if (!format) {
          return nextPayload
        }

        const formatted = format(nextPayload)
        return formatted || nextPayload
      }

      let payload = removeUndefinedFields(result.formData)
      if (!payload || typeof payload !== "object") {
        payload = {}
      }

      payload = applyFormat(payload)

      const hasCreatedByField =
        Object.prototype.hasOwnProperty.call(payload, "createdBy") ||
        Object.prototype.hasOwnProperty.call(data || {}, "createdBy")

      if (hasCreatedByField) {
        const existingCreatedBy =
          typeof data?.createdBy === "string" ? data.createdBy.trim() : data?.createdBy
        const incomingCreatedBy =
          typeof payload?.createdBy === "string" ? payload.createdBy.trim() : payload?.createdBy
        const resolvedCreatedBy = existingCreatedBy || incomingCreatedBy || auth.currentUser?.uid
        if (resolvedCreatedBy) {
          payload = { ...payload, createdBy: resolvedCreatedBy }
        }
      }

      if (onAdd) {
        return onAdd(payload)
      }

      // `mainId` points to the parent document (e.g. sponsor id), not the row being saved.
      // Falling back to it here causes nested creates to try updating a non-existent doc.
      let id = data?.id ?? formData?.id
      const collectionInfo = Service.getListLocation(mainId)

      if (Array.isArray(collectionInfo)) {
        const collectionRef = collection(db, collectionInfo.join("/"))
        const docRef = id ? doc(db, collectionInfo.join("/"), id) : doc(collectionRef)
        id = id || docRef.id

        if (Service.imageProcessor) {
          payload = await Service.imageProcessor(
            payload,
            (docId, fieldName, image, info, imagePropName) =>
              handleUpload(docId, fieldName, image, info, imagePropName),
            id,
            mainId,
          )
        }

        payload = removeUndefinedFields(applyFormat(payload))

        if (data?.id ?? formData?.id) {
          await updateDoc(docRef, { id, ...payload })
        } else {
          await setDoc(docRef, { id, ...payload })
        }
      }

      if (typeof close === "function") {
        close()
      }

      onAdded && onAdded()
    } catch (error: any) {
      PopupService.pushPopup(error?.message || "Unable to save data")
    } finally {
      LoadingService.setLoading(false)
    }
  }

  const formContext = useMemo(
    () => ({
      formData,
      updateFormData: (patch: Record<string, unknown>) =>
        setFormData((prev: any) => ({ ...prev, ...patch })),
    }),
    [formData],
  )

  return (
    <Form
      formData={formData || {}}
      templates={{ ObjectFieldTemplate: Service.ObjectFieldTemplate }}
      schema={schema}
      fields={Service.fields}
      uiSchema={Service.uiSchema}
      validator={validator}
      onChange={(event) => setFormData(event.formData)}
      onSubmit={handleSubmit as any}
      widgets={Service.widgets}
      formContext={formContext}
      omitExtraData
      liveOmit
    />
  )
}

export { AddEditComponent }
